"use client";

import { cn } from "@ui/lib";
import { ArrowRightIcon, ImageUpIcon, WandSparklesIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
	VIDEO_HERO_EXAMPLE_EVENT,
	VIDEO_HERO_ID,
	VIDEO_HERO_IMAGES_EVENT,
	VIDEO_HERO_MODE_EVENT,
	VIDEO_HERO_PROMPT_ID,
	VIDEO_HERO_TEMPLATE_EVENT,
} from "./HeroControls";
import type { HeroMode, HeroShowcaseExample } from "./VideoGeneratorHero";

export function scrollToGenerator() {
	document
		.getElementById(VIDEO_HERO_ID)
		?.scrollIntoView({ behavior: "smooth", block: "start" });
	document
		.getElementById(VIDEO_HERO_PROMPT_ID)
		?.focus({ preventScroll: true });
}

/** Seedance 样片：回填模式、模型、提示词、参考图和画幅，再滚回生成器。 */
export function UseShowcaseExampleButton({
	example,
	label,
	className,
}: {
	example: HeroShowcaseExample;
	label: string;
	className?: string;
}) {
	return (
		<button
			type="button"
			onClick={() => {
				window.dispatchEvent(
					new CustomEvent(VIDEO_HERO_EXAMPLE_EVENT, {
						detail: example,
					}),
				);
				scrollToGenerator();
			}}
			className={className}
		>
			<WandSparklesIcon className="size-4" aria-hidden />
			{label}
		</button>
	);
}

/** 底部 CTA：滚回首页 hero 生成器并把光标放进提示词框 */
export function ScrollToGeneratorButton({ label }: { label: string }) {
	return (
		<button
			type="button"
			onClick={scrollToGenerator}
			className="flex h-14 items-center justify-center gap-2 rounded-full bg-brand px-8 font-semibold text-base text-white shadow-cta transition-colors hover:bg-brand-500 lg:h-[70px] lg:px-12 lg:text-lg"
		>
			{label}
			<ArrowRightIcon className="size-5" />
		</button>
	);
}

/**
 * 手机吸底 CTA：hero 生成器滑出屏幕后出现，底部 CTA 区（data-sticky-cta-stop）或页脚
 * 进屏就收起，那里自己有按钮。手机上整页 20 多屏，中间没有别的生成入口。
 */
export function MobileStickyCta({ label }: { label: string }) {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const targets = [
			document.getElementById(VIDEO_HERO_ID),
			...Array.from(
				document.querySelectorAll("[data-sticky-cta-stop], footer"),
			),
		].filter((el): el is HTMLElement => el instanceof HTMLElement);
		if (!targets.length) {
			return;
		}
		const onScreen = new Set<Element>();
		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					onScreen.add(entry.target);
				} else {
					onScreen.delete(entry.target);
				}
			}
			setVisible(onScreen.size === 0);
		});
		for (const el of targets) {
			observer.observe(el);
		}
		return () => observer.disconnect();
	}, []);

	return (
		<div
			aria-hidden={!visible}
			className={cn(
				"fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-background via-background/80 to-transparent px-4 pt-6 pb-[max(12px,env(safe-area-inset-bottom))] transition-all duration-300 sm:hidden",
				visible
					? "translate-y-0 opacity-100"
					: "pointer-events-none translate-y-full opacity-0",
			)}
		>
			<button
				type="button"
				tabIndex={visible ? 0 : -1}
				onClick={scrollToGenerator}
				className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand font-semibold text-base text-white shadow-card"
			>
				{label}
				<ArrowRightIcon className="size-5" />
			</button>
		</div>
	);
}

/** 模式卡的 Try it：先让 hero 切到对应 tab，再滚回去 */
export function TryHeroModeButton({
	mode,
	label,
}: {
	mode: HeroMode;
	label: string;
}) {
	return (
		<button
			type="button"
			onClick={() => {
				window.dispatchEvent(
					new CustomEvent(VIDEO_HERO_MODE_EVENT, { detail: mode }),
				);
				scrollToGenerator();
			}}
			className="-mx-2 -mb-3 mt-1 inline-flex min-h-11 cursor-pointer items-center px-2 font-medium text-brand text-sm hover:underline"
		>
			{label}
		</button>
	);
}

/** 样片上的按钮：把这条样片的原图填进 hero 的照片槽（有模板 id 先切模板），再滚回去 */
export function UseShowcaseImagesButton({
	images,
	templateId,
	label,
	className,
}: {
	images: string[];
	templateId?: string;
	label: string;
	className?: string;
}) {
	return (
		<button
			type="button"
			onClick={() => {
				if (templateId) {
					window.dispatchEvent(
						new CustomEvent(VIDEO_HERO_TEMPLATE_EVENT, {
							detail: templateId,
						}),
					);
				}
				window.dispatchEvent(
					new CustomEvent(VIDEO_HERO_IMAGES_EVENT, {
						detail: images,
					}),
				);
				scrollToGenerator();
			}}
			className={className}
		>
			<ImageUpIcon className="size-4" />
			{label}
		</button>
	);
}
